import type { ChangeEvent, ReactNode } from 'react';
import { useId, useRef, useState } from 'react';
import { Button } from './Button.js';
import { cn } from './cn.js';
import { Icon } from './Icon.js';

/**
 * Captura de documento: câmera no telefone, arquivo no computador, um controle só.
 *
 * ## Por que ele nasce direto em `packages/ui`
 *
 * A regra do `PADROES.md` é promover para o pacote quando a **terceira tela**
 * precisar. Ela existe para evitar abstração cedo — não para obrigar a copiar
 * código entre as duas aplicações. Aqui as duas usam desde o primeiro dia: o
 * associado manda o documento pelo portal e quem atende manda pela interna, e
 * as duas gravam pelo mesmo `StorageService` da API,
 * com o mesmo teto e a mesma allowlist do servidor. Escrever isto duas vezes é
 * exatamente como nascem duas ideias diferentes de "quanto é grande demais".
 *
 * ## De onde ele veio
 *
 * É o único componente bom preso no `FormularioCancelamento` legado
 * (`app/[cpf]/FotoDocumento.tsx` e `app/[cpf]/comprimirImagem.ts`, lidos do
 * clone em `~/Projetos/evogard/legado/`). A retenção é o primeiro módulo, na
 * ordem das ondas, que precisa de câmera — demandas anexa arquivo e não pede
 * foto —, então ele sobe aqui. O que veio de lá, o que mudou e por quê está
 * dito em cada decisão abaixo.
 *
 * ## Câmera: o input nativo, não `getUserMedia`
 *
 * O legado desenhava a própria câmera com `getUserMedia`, `<video>` e um botão
 * de virar a lente — e por isso tinha **duas** interfaces: "Tirar foto" (a
 * câmera dele) e "Escolher arquivo" (o input). Aqui é uma só, e é o input.
 *
 * - `getUserMedia` pede permissão persistente de câmera, exige HTTPS, desenha a
 *   própria prévia e obriga a tratar orientação, lente frontal × traseira e o
 *   encerramento das faixas no desmonte. Cada um desses é um caso especial que
 *   já existe pronto no sistema operacional.
 * - O input nativo resolve foco, permissão, orientação e a prévia do próprio
 *   aparelho, e funciona no iOS sem caso especial — que é onde o legado mais
 *   apanhou.
 *
 * **E ele NÃO leva o atributo `capture`**, apesar de `capture` ser o caminho
 * mais curto para abrir a câmera. Com `capture`, o telefone abre a câmera
 * **direto** e some com a galeria e com os arquivos: quem já fotografou o
 * documento ontem não consegue usar a foto, e o PDF fica inalcançável. O
 * comentário do próprio legado registra essa medição — foi por isso que eles
 * tiraram o atributo. Sem `capture`, o iOS oferece "Tirar foto" na primeira
 * linha da folha de ação e o Android abre o seletor com a câmera junto: a
 * câmera continua a um toque, e o resto não desaparece.
 *
 * ## Compressão, e por que ela é obrigatória e não uma otimização
 *
 * O associado fotografa o documento com a câmera do celular e produz 8 MB de
 * JPEG — que sobem por uma rede móvel ruim até o servidor recusar por tamanho,
 * ou até o Safari abortar a conexão e a tela dizer "Load failed". E o iPhone
 * entrega **HEIC**, que não está na allowlist do
 * `apps/api/src/common/storage.service.ts`: cru, ele seria recusado por
 * tipo mesmo cabendo no teto. Passar pelo canvas resolve os dois — reduz e sai
 * JPEG.
 *
 * ## Uma diferença deliberada em relação ao legado
 *
 * O `comprimirImagem` legado devolvia **o arquivo original** quando qualquer
 * coisa dava errado, com o comentário "nunca bloqueia o envio". Aqui isso seria
 * a mentira de interface que a casa proíbe: o original pode ser HEIC, e deixar
 * enviar é deixar a pessoa esperar o upload inteiro para receber uma recusa do
 * servidor. Quando o canvas falha, este componente só segue em frente se o
 * arquivo **já** for de um tipo que o servidor aceita e couber no teto
 * (`canProceedWithoutCompressing`); fora disso ele recusa na hora, com o motivo.
 *
 * ## O que ele não faz, e diz que não faz
 *
 * PDF **não passa por canvas** — não existe como reduzir um PDF no navegador
 * sem embutir um renderizador. Ele vai **como está**, e a tela diz isso com
 * essas palavras, junto com o teto. Também não há prévia de PDF: a tela mostra
 * nome e tamanho e afirma que não dá para ver o conteúdo ali. Um PDF de 20 MB
 * aceito em silêncio e recusado pelo servidor depois é o defeito que este
 * parágrafo existe para não deixar acontecer.
 */

/* ------------------------------------------------------------------------- *
 * As constantes, e o motivo de cada número
 * ------------------------------------------------------------------------- */

/**
 * O lado maior da imagem depois de reduzida, em pixels.
 *
 * 1600 é o valor que o `FormularioCancelamento` roda **em produção** para este
 * mesmo documento — CNH e RG fotografados por associado —, e a foto sai entre
 * 200 e 600 KB. Não é um número escolhido no olho aqui: é o único desta lista
 * que tem uso medido atrás dele.
 *
 * O que o faria mudar: alguém da operação não conseguir ler um campo do
 * documento na prévia assinada. Aí ele sobe (2048), não desce — e o teste que
 * acompanha a mudança é ler o número do documento na imagem gravada.
 *
 * **E o legado já mudou para uma das fotos** (v27): a do documento ABERTO —
 * frente e verso numa foto só, letra miúda — sai com 2400 px e qualidade 0.92
 * (`enviarFoto(arquivoDocumento, …, 2400, 0.92)`). A selfie continua em 1600.
 * Quem precisa disso passa `maxLongSide` e `quality`; o padrão fica aqui.
 */
export const MAX_LONG_SIDE = 1600;

/**
 * A qualidade do JPEG de saída, de 0 a 1.
 *
 * 0.8, também herdado da produção do legado. Acima disso (0.9) o arquivo quase
 * dobra sem ganho visível em texto impresso; abaixo de 0.7 o artefato do JPEG
 * começa a comer justamente o que importa — o traço fino de um número de
 * documento. É o intervalo onde 0.8 é o meio, não uma preferência.
 */
export const JPEG_QUALITY = 0.8;

/**
 * O teto do que o servidor aceita guardar, em bytes.
 *
 * **É uma cópia**, e a cópia é declarada: `packages/ui` não depende da API e
 * não deve depender. O número verdadeiro é o `MAX_SIZE` de
 * `apps/api/src/common/storage.service.ts`, e quem impede os dois de
 * divergirem em silêncio é `DocumentCapture.test.ts`, que lê aquele arquivo do
 * disco e compara. Sem essa trava, a tela prometeria 20 MB depois de o servidor
 * ter passado a aceitar 10 — e a mentira só apareceria no upload de alguém.
 */
export const MAX_BYTES = 20 * 1024 * 1024;

/**
 * A foto que não dá para ler — o `validarFoto.ts` do `FormularioCancelamento`.
 *
 * Em produção chegaram fotos do documento 100% pretas (print da CNH Digital e
 * do gov.br, que bloqueiam captura de tela) e de 2×2 px. Os limites são os de
 * lá, calibrados nas fotos já recebidas: a menor foto real tinha 177 px no
 * menor lado, e a mais escura um desvio de luminância perto de 15; as pretas
 * têm 0.
 */
export const MIN_READABLE_SIDE = 100;
export const MIN_LUMINANCE_DEVIATION = 3;
export const PHOTO_TOO_SMALL =
  'A imagem é pequena demais para ler o documento. Tire uma nova foto.';
export const PHOTO_BLANK =
  'A imagem ficou toda preta ou em branco. Prints de aplicativos como CNH Digital ou ' +
  'gov.br costumam sair pretos — tire uma foto do documento físico.';

/** O lado menor abaixo do que se lê. */
export function tooSmallToRead(width: number, height: number): boolean {
  return Math.min(width, height) < MIN_READABLE_SIDE;
}

/**
 * O desvio-padrão da luminância de uma amostra RGBA. Uma cor só (toda preta,
 * toda branca) dá perto de zero.
 */
export function luminanceDeviation(rgba: ArrayLike<number>): number {
  const n = Math.floor(rgba.length / 4);
  if (n === 0) return 0;
  let sum = 0;
  let squares = 0;
  for (let i = 0; i < n * 4; i += 4) {
    const y = 0.299 * rgba[i]! + 0.587 * rgba[i + 1]! + 0.114 * rgba[i + 2]!;
    sum += y;
    squares += y * y;
  }
  const mean = sum / n;
  return Math.sqrt(Math.max(0, squares / n - mean * mean));
}

/** O que o servidor aceita sem passar pelo canvas. Espelha a allowlist dele. */
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export const PDF_TYPE = 'application/pdf';

/* ------------------------------------------------------------------------- *
 * As decisões, como funções puras — é o que dá para medir sem navegador
 * ------------------------------------------------------------------------- */

/**
 * O tipo que vale, resolvido também pela extensão.
 *
 * O `type` do `File` é palpite do aparelho, e em parte dos Android ele vem
 * **vazio** — nem "desconhecido", vazio. Cair direto na recusa nesse caso
 * mandaria a pessoa embora com uma foto perfeitamente boa na mão. A extensão é
 * o segundo palpite, e quem decide de verdade é o servidor, que confere a
 * assinatura do conteúdo.
 */
export function effectiveType(typeMime: string, nome: string): string {
  if (typeMime) return typeMime.toLowerCase();
  const extension = /\.([a-z0-9]+)$/i.exec(nome)?.[1]?.toLowerCase();
  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'heic':
    case 'heif':
      return 'image/heic';
    case 'pdf':
      return PDF_TYPE;
    default:
      return '';
  }
}

/** O caminho que o arquivo escolhido segue — e, quando não segue, o motivo. */
export type Track =
  | { trail: 'comprimir' }
  | { trail: 'como-esta'; notice: string }
  | { trail: 'recusado'; reason: string };

/**
 * Para onde vai o arquivo que a pessoa acabou de escolher.
 *
 * Imagem vai para o canvas **sem olhar o tamanho de origem**, de propósito: uma
 * foto de 8 MB vira 400 KB, e recusá-la na escolha seria recusar o caso normal.
 * Quem confere o teto na trilha da imagem é o passo depois da compressão.
 *
 * PDF é o contrário — não há passo nenhum entre escolher e enviar, então o teto
 * vale aqui, e a recusa sai antes de a pessoa esperar por um upload que já
 * está condenado.
 */
export function classifyChoice(
  tipo: string,
  sizeBytes: number,
  options: { acceptsPdf: boolean; capBytes?: number },
): Track {
  const cap = options.capBytes ?? MAX_BYTES;

  if (tipo.startsWith('image/')) return { trail: 'comprimir' };

  if (tipo === PDF_TYPE) {
    if (!options.acceptsPdf) {
      return {
        trail: 'recusado',
        reason:
          'Aqui entra foto do documento. Para PDF, use o campo de anexo da solicitação.',
      };
    }
    if (sizeBytes > cap) {
      return {
        trail: 'recusado',
        reason:
          `Este PDF tem ${formatBytes(sizeBytes)} e o limite é ${formatBytes(cap)}. ` +
          'PDF não é reduzido aqui — ele vai como está.',
      };
    }
    return {
      trail: 'como-esta',
      notice: 'PDF vai como está: não dá para reduzir um PDF no navegador.',
    };
  }

  return {
    trail: 'recusado',
    reason: options.acceptsPdf
      ? 'Formato não aceito. Envie uma foto (JPG, PNG ou WebP) ou um PDF.'
      : 'Formato não aceito. Envie uma foto (JPG, PNG ou WebP).',
  };
}

/**
 * O tamanho de destino, com o lado maior no teto e a proporção mantida.
 *
 * Nunca aumenta: uma foto já pequena passaria a ocupar mais bytes para mostrar
 * a mesma coisa borrada.
 */
export function reducedDimension(
  width: number,
  height: number,
  cap: number = MAX_LONG_SIDE,
): { width: number; height: number } {
  const scale = Math.min(1, cap / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Se, com o canvas fora do ar, ainda dá para enviar o arquivo original.
 *
 * Só quando ele já é um dos tipos da allowlist do servidor **e** cabe no teto.
 * É o ponto exato onde este componente diverge do legado, que devolvia o
 * original sempre — inclusive HEIC, que o servidor recusa.
 */
export function canProceedWithoutCompressing(
  tipo: string,
  sizeBytes: number,
  capBytes: number = MAX_BYTES,
): boolean {
  return (
    (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(tipo) && sizeBytes <= capBytes
  );
}

/**
 * O base64 puro de uma data URL — que é o que o campo `content` das rotas de
 * anexo pede. Mandar a data URL inteira faria o servidor gravar `data:image/jpeg;base64,`
 * como se fosse conteúdo, e a assinatura do arquivo não bateria.
 */
export function contentFromDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  const header = comma === -1 ? '' : dataUrl.slice(0, comma);
  if (!header.startsWith('data:') || !header.includes(';base64')) {
    throw new Error('A leitura do arquivo não devolveu base64.');
  }
  return dataUrl.slice(comma + 1);
}

/** Quantos bytes um base64 representa. Serve para dizer o tamanho do que VAI. */
export function bytesFromBase64(base64: string): number {
  if (base64.length === 0) return 0;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return (base64.length / 4) * 3 - padding;
}

/**
 * Tamanho em pt-BR.
 *
 * A base é 1024, e o rótulo é "MB" — a mesma escolha (e a mesma imprecisão) da
 * mensagem de recusa do servidor. Duas unidades diferentes para o mesmo limite
 * dariam "19,1 MB" na tela e "20 MB" na recusa, para o mesmo arquivo.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  // `20,0 MB` num limite redondo lê como precisão que não existe; `12,3 MB` num
  // arquivo de verdade é a precisão que a pessoa usa para decidir se corta.
  return `${mb.toFixed(1).replace(/\.0$/, '').replace('.', ',')} MB`;
}

/** A foto sai do canvas como JPEG; o nome precisa dizer a verdade nova. */
export function nameAsJpeg(nome: string): string {
  const withoutExtension = nome.replace(/\.[^./\\]+$/, '');
  return `${withoutExtension || 'documento'}.jpg`;
}

/**
 * As classes do gatilho, expostas para o teste de toque.
 *
 * O gatilho é escrito à mão — não é `Button`, porque quem recebe o clique é o
 * `<input type="file">` sobreposto, e um `<button>` só faria o clique dar a
 * volta pelo JavaScript. Escrito à mão, ele fica fora do `touch.test.ts`, que
 * cobre o que `buttonClasses` e `linkTextClasses` produzem — foi assim que um
 * link de 17px passou em revisão em 26/08. Por isso a régua de 44px é medida
 * aqui, sobre esta função.
 */
export function triggerClasses(disabled = false): string {
  return cn(
    'relative flex min-h-11 w-full items-center justify-center gap-2 rounded-borda',
    'border border-dashed border-borda-forte bg-superficie px-4 py-3',
    'text-sm font-medium text-texto transition-[border-color,background-color]',
    'pointer-coarse:min-h-11 pointer-coarse:px-4',
    'hover:border-texto-suave hover:bg-superficie-suave',
    'has-[:focus-visible]:outline has-[:focus-visible]:outline-2',
    'has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-foco',
    disabled && 'cursor-not-allowed opacity-60',
  );
}

/* ------------------------------------------------------------------------- *
 * O que sai daqui para quem monta a tela
 * ------------------------------------------------------------------------- */

/** O arquivo pronto para subir: o `content` em base64 que as rotas de anexo da casa recebem. */
export interface CapturedDocument {
  nome: string;
  typeMime: string;
  /** o arquivo em base64, sem o prefixo `data:` */
  content: string;
  /** o tamanho do que VAI, não do que a pessoa escolheu */
  sizeBytes: number;
}

export interface DocumentCaptureProps {
  /** Rótulo visível. Placeholder como rótulo some quando a pessoa mais precisa. */
  label: string;
  value: CapturedDocument | null;
  onChange: (document: CapturedDocument | null) => void;
  /** Erro vindo de fora — do envio, da validação do formulário. */
  error?: string | null;
  help?: ReactNode;
  /** PDF é aceito e vai sem compressão. Falso deixa só foto, e a tela diz. */
  acceptsPdf?: boolean;
  /** O lado maior depois de reduzida — ver `MAX_LONG_SIDE`. */
  maxLongSide?: number;
  /** A qualidade do JPEG de saída — ver `JPEG_QUALITY`. */
  quality?: number;
  /**
   * Recusa a foto que não dá para ler — pequena demais ou de uma cor só
   * (`MIN_READABLE_SIDE`, `MIN_LUMINANCE_DEVIATION`). Só olha o que o
   * navegador conseguiu decodificar: na dúvida, deixa passar.
   */
  rejectUnreadable?: boolean;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
}

/* ------------------------------------------------------------------------- *
 * O trecho que toca o navegador
 * ------------------------------------------------------------------------- */

/** Decodifica o arquivo. `createImageBitmap` primeiro, `<img>` como reserva. */
async function loadImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // Formato que o decodificador rápido não abre. Cai para o `<img>`.
    }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível abrir esta imagem.'));
    };
    img.src = url;
  });
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.readAsDataURL(file);
  });
}

let canvasTrusted: boolean | null = null;

/**
 * Se o navegador devolve os pixels que o canvas desenhou. Firefox e Brave
 * podem adulterar a leitura (proteção contra rastreamento), e aí a conta da
 * luminância mente — a foto passa, como no legado: nunca se recusa por falha
 * nossa.
 */
function canvasReadable(): boolean {
  if (canvasTrusted !== null) return canvasTrusted;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 1;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return (canvasTrusted = false);
    context.fillStyle = 'black';
    context.fillRect(0, 0, 1, 1);
    context.fillStyle = 'white';
    context.fillRect(1, 0, 1, 1);
    const d = context.getImageData(0, 0, 2, 1).data;
    canvasTrusted =
      d[0]! < 16 && d[1]! < 16 && d[2]! < 16 && d[4]! > 239 && d[5]! > 239 && d[6]! > 239;
  } catch {
    canvasTrusted = false;
  }
  return canvasTrusted;
}

/** Por que a foto não dá para ler — ou nulo. Amostra em 64×64, como o legado. */
function unreadableReason(origin: ImageBitmap | HTMLImageElement): string | null {
  const { width, height } = origin;
  if (!width || !height) return null;
  if (tooSmallToRead(width, height)) return PHOTO_TOO_SMALL;
  if (!canvasReadable()) return null;
  try {
    const sample = document.createElement('canvas');
    sample.width = 64;
    sample.height = 64;
    const context = sample.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(origin as CanvasImageSource, 0, 0, width, height, 0, 0, 64, 64);
    const deviation = luminanceDeviation(context.getImageData(0, 0, 64, 64).data);
    return deviation < MIN_LUMINANCE_DEVIATION ? PHOTO_BLANK : null;
  } catch {
    return null;
  }
}

/**
 * Redimensiona e recodifica em JPEG. Devolve `null` quando o canvas não deu, e
 * o motivo quando `check` pediu para olhar e a foto não dá para ler.
 */
async function compressImage(
  file: File,
  options: { cap: number; quality: number; check: boolean },
): Promise<{ blob: Blob } | { unreadable: string } | null> {
  try {
    const origin = await loadImage(file);
    if (options.check) {
      const reason = unreadableReason(origin);
      if (reason !== null) {
        if ('close' in origin) origin.close();
        return { unreadable: reason };
      }
    }
    const { width, height } = reducedDimension(origin.width, origin.height, options.cap);

    const screen = document.createElement('canvas');
    screen.width = width;
    screen.height = height;
    const context = screen.getContext('2d');
    if (!context) return null;
    context.drawImage(origin as CanvasImageSource, 0, 0, width, height);
    if ('close' in origin) origin.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      screen.toBlob(resolve, 'image/jpeg', options.quality),
    );
    return blob ? { blob } : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------------- *
 * O componente
 * ------------------------------------------------------------------------- */

export function DocumentCapture({
  label,
  value,
  onChange,
  error,
  help,
  acceptsPdf = true,
  maxLongSide = MAX_LONG_SIDE,
  quality = JPEG_QUALITY,
  rejectUnreadable = false,
  disabled = false,
  required = false,
  id,
  className,
}: DocumentCaptureProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const idHelp = `${inputId}-ajuda`;
  const idFormat = `${inputId}-formato`;
  const idError = `${inputId}-erro`;

  const entry = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * A nota do que aconteceu com o arquivo — "reduzida de 8 MB para 420 KB",
   * "vai como está". É a única coisa que fica em estado: ela só é sabida no
   * instante do processamento e não dá para reconstruí-la depois.
   *
   * **A prévia NÃO fica aqui**, e a primeira versão errava nisso: ela guardava
   * a data URL em estado, e um `valor` vindo de fora (um rascunho restaurado,
   * um formulário remontado) chegava sem prévia — a tela mostrava o bloco "não
   * dá para ver o conteúdo do PDF aqui" para uma foto. A prévia é derivada de
   * `valor`, que é quem sabe a verdade.
   */
  const [note, setNote] = useState<string | null>(null);

  /*
   * A prévia é DERIVADA de `valor`, não guardada — o base64 já está aqui, e uma
   * data URL não precisa de `URL.revokeObjectURL` nem de um `useEffect` de
   * limpeza que alguém esquece. Só imagem tem prévia; o resto (PDF) cai no
   * bloco que diz que não tem.
   */
  const previewUrl =
    value && value.typeMime.startsWith('image/')
      ? `data:${value.typeMime};base64,${value.content}`
      : null;

  // O erro que a tela mostra é o de fora ou o de dentro — nunca os dois somados,
  // que produziria duas frases dizendo a mesma coisa com palavras diferentes.
  const errorVisible = error ?? failure;

  const descricao = [help ? idHelp : null, idFormat, errorVisible ? idError : null]
    .filter(Boolean)
    .join(' ');

  function clear() {
    setFailure(null);
    setNote(null);
    onChange(null);
    // Sem isto, escolher o MESMO arquivo de novo não dispara `change`, e o
    // "Refazer" fica mudo exatamente para quem tirou a foto de novo do zero.
    if (entry.current) entry.current.value = '';
  }

  async function toChoose(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setFailure(null);
    setNote(null);
    onChange(null);
    setProcessing(true);

    try {
      const tipo = effectiveType(file.type, file.name);
      const destination = classifyChoice(tipo, file.size, { acceptsPdf });

      if (destination.trail === 'recusado') {
        setFailure(destination.reason);
        return;
      }

      if (destination.trail === 'como-esta') {
        const content = contentFromDataUrl(await readAsDataUrl(file));
        const sizeBytes = bytesFromBase64(content);
        onChange({ nome: file.name, typeMime: tipo, content, sizeBytes });
        setNote(destination.notice);
        return;
      }

      const outcome = await compressImage(file, {
        cap: maxLongSide,
        quality,
        check: rejectUnreadable,
      });

      if (outcome && 'unreadable' in outcome) {
        setFailure(outcome.unreadable);
        return;
      }
      const compressed = outcome?.blob ?? null;

      if (!compressed) {
        // O canvas não deu. Só segue quem o servidor aceitaria do jeito que está.
        if (!canProceedWithoutCompressing(tipo, file.size)) {
          setFailure(
            'Não foi possível preparar esta foto neste navegador. ' +
              'Tente tirar outra pela câmera ou escolher um JPG, PNG ou WebP.',
          );
          return;
        }
        const content = contentFromDataUrl(await readAsDataUrl(file));
        onChange({
          nome: file.name,
          typeMime: tipo,
          content,
          sizeBytes: bytesFromBase64(content),
        });
        setNote('Esta foto vai como está: não deu para reduzi-la neste navegador.');
        return;
      }

      const content = contentFromDataUrl(await readAsDataUrl(compressed));
      const sizeBytes = bytesFromBase64(content);

      if (sizeBytes > MAX_BYTES) {
        setFailure(
          `Mesmo reduzida, esta foto ficou com ${formatBytes(sizeBytes)} e o ` +
            `limite é ${formatBytes(MAX_BYTES)}.`,
        );
        return;
      }

      onChange({
        nome: nameAsJpeg(file.name),
        typeMime: 'image/jpeg',
        content,
        sizeBytes,
      });
      setNote(
        `Reduzida de ${formatBytes(file.size)} para ` +
          `${formatBytes(sizeBytes)} no seu aparelho, antes de enviar.`,
      );
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'Não foi possível ler este arquivo.');
    } finally {
      setProcessing(false);
      // A prévia já carrega o arquivo; deixar o input preenchido faria o próximo
      // "Refazer" com a MESMA foto não disparar `change`.
      if (entry.current) entry.current.value = '';
    }
  }

  const formats = acceptsPdf
    ? `Foto ou PDF, até ${formatBytes(MAX_BYTES)}. A foto é reduzida no seu aparelho ` +
      'antes de enviar; o PDF vai como está.'
    : `Foto, até ${formatBytes(MAX_BYTES)}. Ela é reduzida no seu aparelho antes de enviar.`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={inputId} className="text-sm font-medium text-texto">
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </label>

      {/*
       * O que ocupa o lugar da prévia enquanto o canvas trabalha é um ESQUELETO,
       * não um giro no meio da tela: é a régua do DESIGN.md, e aqui ela também
       * é honesta com o tempo — numa foto de 12 MP o passo leva segundos num
       * telefone modesto, e um giro não diz onde a coisa vai aparecer.
       *
       * A tabela de estados do DESIGN.md diz que campo não tem "carregando" —
       * ela fala do giro DENTRO da caixa de texto. Este controle produz um
       * artefato, e o lugar dele existe antes de ele existir.
       */}
      <div aria-busy={processing || undefined}>
        {processing && (
          <div className="flex flex-col gap-2">
            <span className="esqueleto block h-40 w-full" />
            <span className="esqueleto block h-4 w-48" />
            <p role="status" className="sr-only">
              Preparando a imagem…
            </p>
          </div>
        )}

        {!processing && value && (
          <div className="flex flex-col gap-2">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={`Prévia de ${value.nome}`}
                className="max-h-64 w-full rounded-borda border border-borda bg-superficie-suave object-contain"
              />
            ) : (
              /*
               * PDF não tem prévia, e a tela DIZ que não tem — em vez de um
               * quadrado cinza que a pessoa fica esperando carregar.
               */
              <div className="flex items-center gap-3 rounded-borda border border-borda bg-superficie-suave px-3 py-3">
                <Icon nome="documento" size={24} className="text-texto-suave" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-texto">{value.nome}</p>
                  <p className="text-xs text-texto-suave">
                    Não dá para ver o conteúdo do PDF aqui.
                  </p>
                </div>
              </div>
            )}

            <p className="text-xs text-texto-suave">
              <span className="font-medium text-texto">
                {formatBytes(value.sizeBytes)}
              </span>
              {note ? ` · ${note}` : null}
            </p>
          </div>
        )}
      </div>

      {/*
       * Um gatilho só, sempre montado, e o `<input>` sobreposto a ele.
       *
       * Sobreposto, e não escondido com um `.click()` no JavaScript: assim o
       * clique, o toque, o Tab e o Enter são os nativos, e o rótulo acima é o
       * nome acessível do controle sem precisar de `aria-label` nenhum. O que
       * muda entre "ainda não escolhi" e "quero trocar" é o texto — não o
       * mecanismo, que é o mesmo dos dois lados.
       */}
      <div className={triggerClasses(disabled)}>
        <input
          ref={entry}
          id={inputId}
          type="file"
          accept={acceptsPdf ? `image/*,${PDF_TYPE}` : 'image/*'}
          disabled={disabled || processing}
          required={required && !value}
          aria-invalid={errorVisible ? true : undefined}
          aria-describedby={descricao}
          onChange={toChoose}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
        <Icon nome="camera" className="text-texto-suave" />
        <span>
          {value
            ? 'Refazer — tirar outra foto ou trocar o arquivo'
            : 'Tirar foto ou escolher arquivo'}
        </span>
      </div>

      {value && (
        <div>
          <Button
            variant="discreto"
            size="sm"
            icon="fechar"
            onClick={clear}
            disabled={disabled}
          >
            Remover
          </Button>
        </div>
      )}

      {help && (
        <p id={idHelp} className="text-xs text-texto-suave">
          {help}
        </p>
      )}

      <p id={idFormat} className="text-xs text-texto-suave">
        {formats}
      </p>

      {errorVisible && (
        <p id={idError} className="flex items-start gap-1 text-xs font-medium text-erro">
          <Icon nome="atencao" size={16} className="mt-px" />
          {errorVisible}
        </p>
      )}
    </div>
  );
}
